import {
  Button,
  ButtonProps,
  Drawer as ChakraDrawer,
  DrawerBody,
  DrawerContent as ChakraDrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Icon,
  IconProps,
  UseDisclosureProps,
} from '@chakra-ui/react';
import React, { ReactNode } from 'react';
import { KText } from '@/ui-library/components/text';
import { ModalCloseButton } from '@/ui/ModalClosebutton';

interface MoreCtaConfig {
  icon?: any;
  id: string;
  label?: string;
  onClick?: () => void;
  props?: ButtonProps;
  Comp?: React.ComponentType<any>;
  iconProps?: IconProps;
  isDisabled?: boolean;
}
interface DrawerProps {
  drawerActions: UseDisclosureProps;
  headerText?: ReactNode;
  headerDescription?: string;
  isCtaLoading?: boolean;
  headerTextProps?: any;
  headerDescripitionColor?: string;
  showFooter?: boolean;
  showHeader?: boolean;
  onDrawerSubmit?: () => void;
  secondaryCtaLabel?: string;
  secondaryCtaClick?: (onClose: () => void) => void;
  children: ReactNode;
  ctaLabel?: string;
  drawerContentProps?: DrawerContentProps;
  drawerBodyProps?: any;
  drawerFooterProps?: any;
  moreCtaConfig?: MoreCtaConfig[];
  disableCancel?: boolean;
  ctaProps?: ButtonProps;
  headerRightAdditionalComponent?: any;
  hasFooterDivider?: boolean;
  drawerRef?: React.RefObject<HTMLDivElement>;
  chakraDrawerProps?: any;
  loadingText?: string;
  hideCloseButton?: boolean;
}

export const Drawer: React.FC<DrawerProps> = ({
  drawerActions,
  headerText = '',
  headerDescription = '',
  isCtaLoading,
  headerTextProps,
  headerDescripitionColor: _headerDescripitionColor,
  headerRightAdditionalComponent,
  drawerContentProps = {},
  drawerBodyProps = {},
  drawerFooterProps = {},
  showFooter = true,
  showHeader = true,
  onDrawerSubmit = () => {},
  disableCancel = false,
  secondaryCtaLabel = 'Cancel',
  hideCloseButton = false,
  secondaryCtaClick = (onClose) => {
    onClose();
  },
  moreCtaConfig = undefined,
  children,
  ctaLabel = '',
  ctaProps,
  hasFooterDivider = false,
  drawerRef,
  chakraDrawerProps = {},
  loadingText = 'Updating',
}) => {
  const { isOpen, onClose } = drawerActions;

  const renderHeader = () => {
    return (
      <Flex justifyContent="space-between">
        <Flex
          width="100%"
          flexDir="column"
          gap="6px"
          p="1rem"
          {...headerTextProps}
        >
          {typeof headerText === 'string' ? (
            <KText variant="heading_2">{headerText}</KText>
          ) : (
            headerText
          )}
          {headerDescription && <KText>{headerDescription}</KText>}
        </Flex>
        {headerRightAdditionalComponent}
      </Flex>
    );
  };

  const renderFooter = () => {
    return (
      <DrawerFooter
        gap="6px"
        borderTop={hasFooterDivider ? '1px solid' : 'none'}
        borderColor="ats-border-color"
        p="1rem"
        {...drawerFooterProps}
      >
        {moreCtaConfig?.map(
          ({
            id,
            label,
            icon,
            iconProps,
            Comp,
            props,
            onClick,
            isDisabled = false,
          }) => {
            if (Comp) return <Comp key={id} {...props} />;
            return (
              <Button
                key={id}
                variant="secondary"
                onClick={onClick}
                alignItems="center"
                isDisabled={isDisabled}
                {...props}
              >
                {icon && <Icon mr="5px" as={icon} {...iconProps} />}
                {label}
              </Button>
            );
          }
        )}

        {secondaryCtaLabel && !disableCancel && (
          <Button
            variant="secondary"
            data-test-id="drawer-secondary-cta"
            onClick={() => {
              if (secondaryCtaClick) {
                secondaryCtaClick(onClose);
              } else {
                onClose();
              }
            }}
          >
            {secondaryCtaLabel}
          </Button>
        )}
        {ctaLabel && (
          <Button
            isLoading={isCtaLoading}
            loadingText={loadingText}
            onClick={onDrawerSubmit}
            variant="primary"
            {...ctaProps}
            data-test-id="drawer-primary-cta"
          >
            {ctaLabel}
          </Button>
        )}
      </DrawerFooter>
    );
  };

  const renderDrawer = () => {
    return (
      <ChakraDrawer
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
        trapFocus={false}
        closeOnOverlayClick={false}
        {...chakraDrawerProps}
      >
        <DrawerOverlay zIndex="1400" />
        <ChakraDrawerContent
          h="100%"
          minW="604px"
          borderTopLeftRadius="xl"
          p="0"
          m="0"
          {...drawerContentProps}
        >
          {!hideCloseButton && <ModalCloseButton onClose={onClose} />}
          {showHeader && <DrawerHeader p="0">{renderHeader()}</DrawerHeader>}
          <DrawerBody
            h="100%"
            overflow="hidden"
            p="0"
            m="0"
            ref={drawerRef}
            {...drawerBodyProps}
          >
            {children}
          </DrawerBody>
          {showFooter && renderFooter()}
        </ChakraDrawerContent>
      </ChakraDrawer>
    );
  };

  return renderDrawer();
};
